import { useState } from 'react'
import { useLiveQuery } from 'dexie-react-hooks'
import { applyDiscount, runningPromotions, type Order } from '../lib/db'
import { money } from '../lib/money'
import { isOwner, useApp } from '../lib/store'
import { Sheet } from './Sheet'
import { NumPad } from './NumPad'

/**
 * Bớt tiền cho hoá đơn.
 *
 * Hai đường vào, cố ý xếp theo thứ tự ai dùng nhiều hơn thì lên trên:
 *
 *   1. Bấm một chương trình chủ quán đặt sẵn — người bán không phải tính nhẩm, không
 *      phải nhớ hôm nay giảm bao nhiêu. Ai cũng bấm được.
 *   2. Tự gõ số — chỉ chủ quán. Người bán tự bớt tiền tuỳ ý thì cuối ca không ai đối
 *      chiếu được, mà quán nhỏ thì chủ với thợ ngồi cùng một cái quầy.
 */

/** Mấy mức chủ quán hay bớt cho khách quen. Bấm một cái là xong, khỏi gõ. */
const QUICK_PERCENTS = [5, 10, 15, 20]

interface Props {
  order: Order
  onClose: () => void
}

export function DiscountSheet({ order, onClose }: Props) {
  const say = useApp((s) => s.say)
  const owner = isOwner(useApp((s) => s.session?.role))

  const [amount, setAmount] = useState(order.discountPercent ? 0 : (order.discount ?? 0))

  const subtotal = order.subtotal
  const promos = useLiveQuery(() => runningPromotions(subtotal), [subtotal], [])

  async function pickPromotion(id: string, name: string, discount: number) {
    await applyDiscount(order.id, { amount: discount, label: name, promotionId: id })
    say(`${name} · bớt ${money(discount)}đ`)
    onClose()
  }

  async function pickPercent(percent: number) {
    await applyDiscount(order.id, { percent, label: `Giảm ${percent}%` })
    onClose()
  }

  async function pickAmount() {
    // Chưa gõ gì mà bấm Xong là đóng tấm trượt, không phải báo lỗi:
    // người ta mở ra xem có chương trình nào rồi đổi ý thì thôi.
    if (amount <= 0) {
      onClose()
      return
    }
    await applyDiscount(order.id, { amount, label: 'Giảm giá' })
    onClose()
  }

  async function clear() {
    await applyDiscount(order.id, null)
    say('Đã bỏ giảm giá')
    onClose()
  }

  return (
    <Sheet title="Giảm giá" onClose={onClose}>
      <div className="pay-row big">
        <span>Tạm tính</span>
        <b className="num">{money(subtotal)}</b>
      </div>

      {promos.length > 0 && (
        <>
          <h2 className="section-label">Chương trình của quán</h2>
          <div className="list">
            {promos.map(({ promo, discount }) => (
              <button
                key={promo.id}
                type="button"
                className="row-btn"
                disabled={discount === 0}
                aria-pressed={order.promotionId === promo.id}
                onClick={() => void pickPromotion(promo.id, promo.name, discount)}
              >
                <span className="row-icon" aria-hidden="true">
                  🎁
                </span>
                <span className="row-text">
                  <b>{promo.name}</b>
                  <small>
                    {discount > 0
                      ? promo.kind === 'Percent'
                        ? `Bớt ${promo.value}% hoá đơn`
                        : 'Bớt thẳng tiền mặt'
                      : `Hoá đơn từ ${money(promo.minOrderTotal)}đ mới dùng được`}
                  </small>
                </span>
                <span className="row-price num">{discount > 0 ? `−${money(discount)}` : '—'}</span>
              </button>
            ))}
          </div>
        </>
      )}

      {owner ? (
        <>
          <h2 className="section-label">Tự bớt</h2>
          <div className="chip-row">
            {QUICK_PERCENTS.map((p) => (
              <button
                key={p}
                type="button"
                className="chip"
                aria-pressed={order.discountPercent === p}
                onClick={() => void pickPercent(p)}
              >
                {p}%
              </button>
            ))}
          </div>

          <div className="field">
            <span>Hoặc bớt thẳng số tiền</span>
            <div className="amount-readout num">{amount ? money(amount) : '0'}</div>
            <NumPad value={amount} onChange={setAmount} max={subtotal} />
          </div>
        </>
      ) : (
        promos.length === 0 && (
          <div className="empty">
            <div className="empty-icon" aria-hidden="true">
              🎁
            </div>
            <p>Hôm nay quán không có chương trình giảm giá nào.</p>
          </div>
        )
      )}

      <div className="sheet-actions">
        {order.discount > 0 && (
          <button type="button" className="btn btn-danger-ghost" onClick={() => void clear()}>
            Bỏ giảm giá
          </button>
        )}
        {owner && (
          <button type="button" className="btn btn-brand grow" onClick={() => void pickAmount()}>
            {amount > 0 ? `Bớt ${money(amount)}đ` : 'Xong'}
          </button>
        )}
      </div>
    </Sheet>
  )
}
